import { Assets, Spritesheet, Texture } from 'pixi.js';
export class AssetManager {
    renderer;
    artScale;
    generators = new Map();
    textures = new Map();
    loaded = new Map();
    pending = new Map();
    manifests = new Map();
    constructor(renderer, artScale) {
        this.renderer = renderer;
        this.artScale = artScale;
    }
    registerGenerator(name, fn) {
        this.generators.set(name, fn);
    }
    /**
     * Loads an area: entries with priority <= `blockingPriority` are awaited
     * (in priority order, `concurrency` at a time); the rest stream in the
     * background, lowest priority first. Returns when the blocking set is ready.
     */
    async loadArea(manifest, opts = {}) {
        this.manifests.set(manifest.area, manifest);
        const blockingPriority = opts.blockingPriority ?? 0;
        const sorted = [...manifest.entries].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
        const blocking = sorted.filter((e) => (e.priority ?? 0) <= blockingPriority);
        const later = sorted.filter((e) => (e.priority ?? 0) > blockingPriority);
        let done = 0;
        const total = blocking.length;
        await this.runQueue(blocking, opts.concurrency ?? 4, manifest.area, (key) => {
            done++;
            opts.onProgress?.({ loaded: done, total, key });
        });
        const background = this.runQueue(later, 2, manifest.area, () => { });
        return { background };
    }
    /** Lazily ensures specific entries of a registered area are loaded (e.g. an interior when you walk in). */
    async ensure(area, keys) {
        const m = this.manifests.get(area);
        if (!m)
            throw new Error(`AssetManager.ensure: unknown area "${area}"`);
        const entries = m.entries.filter((e) => keys.includes(e.key));
        await this.runQueue(entries, 4, area, () => { });
    }
    has(key) {
        return this.textures.has(key);
    }
    texture(key) {
        const t = this.textures.get(key);
        if (!t)
            throw new Error(`AssetManager: texture "${key}" is not loaded`);
        return t;
    }
    tryTexture(key) {
        return this.textures.get(key);
    }
    /** Texture keys starting with `prefix` (debug tools, variant pickers). */
    keys(prefix = '') {
        const out = [];
        for (const k of this.textures.keys())
            if (k.startsWith(prefix))
                out.push(k);
        return out;
    }
    /** Estimated GPU bytes of everything loaded (decoded RGBA8 + mips). */
    get gpuBytes() {
        let sum = 0;
        for (const e of this.loaded.values())
            sum += e.gpuBytes;
        return sum;
    }
    /**
     * Releases an area's references; entries no other loaded area uses are
     * destroyed (GPU memory freed). Call after the area's display objects are
     * destroyed, never while they are still on stage.
     */
    unloadArea(area) {
        for (const [key, le] of this.loaded) {
            le.refs.delete(area);
            if (le.refs.size > 0)
                continue;
            if (le.entry.kind === 'procedural') {
                // We own these: destroy the frame textures, then the atlas pages (frees GPU memory now).
                for (const tk of le.textureKeys)
                    this.textures.get(tk)?.destroy(false);
                for (const s of le.sources)
                    s.destroy();
            }
            else if (le.entry.src) {
                // Pixi's Assets cache owns URL-loaded textures.
                void Assets.unload(le.entry.src);
            }
            for (const tk of le.textureKeys)
                this.textures.delete(tk);
            this.loaded.delete(key);
        }
        this.manifests.delete(area);
    }
    async runQueue(entries, concurrency, area, onDone) {
        let next = 0;
        const worker = async () => {
            while (next < entries.length) {
                const e = entries[next++];
                await this.loadEntry(e, area);
                onDone(e.key);
            }
        };
        const workers = [];
        for (let i = 0; i < Math.min(concurrency, entries.length); i++)
            workers.push(worker());
        await Promise.all(workers);
    }
    loadEntry(e, area) {
        const existing = this.loaded.get(e.key);
        if (existing) {
            existing.refs.add(area);
            return Promise.resolve();
        }
        const inflight = this.pending.get(e.key);
        if (inflight)
            return inflight.then(() => void this.loaded.get(e.key)?.refs.add(area));
        const p = this.fetchEntry(e).then((le) => {
            le.refs.add(area);
            this.loaded.set(e.key, le);
            this.pending.delete(e.key);
        });
        this.pending.set(e.key, p);
        return p;
    }
    async fetchEntry(e) {
        if (e.kind === 'procedural') {
            const gen = this.generators.get(e.generator ?? e.key);
            if (!gen)
                throw new Error(`AssetManager: no generator "${e.generator ?? e.key}"`);
            const out = await gen({ renderer: this.renderer, artScale: this.artScale });
            for (const [k, t] of out.textures)
                this.textures.set(k, t);
            return { entry: e, textureKeys: [...out.textures.keys()], sources: out.sources, gpuBytes: out.gpuBytes, refs: new Set() };
        }
        if (!e.src)
            throw new Error(`AssetManager: entry "${e.key}" has no src`);
        const res = await Assets.load(e.src);
        if (res instanceof Spritesheet) {
            const keys = [];
            for (const [name, t] of Object.entries(res.textures)) {
                this.textures.set(name, t);
                keys.push(name);
            }
            const src = res.textureSource;
            return { entry: e, textureKeys: keys, sources: [], gpuBytes: src.pixelWidth * src.pixelHeight * 4, refs: new Set() };
        }
        if (res instanceof Texture) {
            this.textures.set(e.key, res);
            return { entry: e, textureKeys: [e.key], sources: [], gpuBytes: res.source.pixelWidth * res.source.pixelHeight * 4, refs: new Set() };
        }
        throw new Error(`AssetManager: "${e.src}" did not load as a texture or spritesheet`);
    }
}
